import { useMemo, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { PlusOutlined, SearchOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { Can } from '@/shared/auth/Can'
import { formatDateTime } from '@/shared/lib/format'
import { shortId } from '@/shared/lib/shortId'
import { EmptyState } from '@/shared/ui/EmptyState'
import { PageHeader } from '@/shared/ui/PageHeader'
import { UrlSearchInput } from '@/shared/ui/UrlSearchInput'
import { useTableUrlState } from '@/shared/ui/table/useTableUrlState'
import { RowActions } from '@/shared/ui/table/RowActions'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import {
  POOL_STATES,
  POOL_STATE_ALL,
  POOL_STATE_FILTER,
  createIpPool,
  fetchActiveByIp,
  fetchIpHistory,
  fetchIpPools,
  poolStateFilter,
  setIpPoolState,
  updateIpPool,
  type IpAllocation,
  type IpPool,
} from '@/features/inventory/ipPoolApi'

/**
 * IP fondi — manzil diapazonlari va ular kimga berilgani.
 *
 * <h2>⚠️ SAHIFA IKKI QISMDAN IBORAT VA IKKINCHISI MUHIMROQ</h2>
 * Chapda fondlarni SOZLASH (diapazon, shlyuz, VLAN) — bu kamdan-kam
 * qilinadigan ish. O'ngda esa "shu manzilda KIM bor" degan savolga
 * javob, va kundalik ishda aynan shu kerak: shikoyat, so'rov yoki
 * huquqiy murojaat IP bilan keladi, operatorga esa abonent kerak.
 *
 * Shuning uchun qidiruv yon panelga emas, sahifaning teng yarmiga
 * qo'yilgan — uni "sozlamalar ichidagi qo'shimcha" qilib qo'yish eng
 * ko'p ishlatiladigan funksiyani eng chuqurga ko'mib qo'yardi.
 *
 * <h2>⚠️ TARIX HAM KO'RSATILADI, faqat hozirgi egasi emas</h2>
 * Manzil vaqt o'tib boshqa abonentga o'tadi. "Shu IP kimda edi
 * o'sha kuni" degan savolga faqat tarix javob beradi va aynan shu
 * savol huquqiy murojaatlarda so'raladi.
 */
export function IpPoolsPage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [editing, setEditing] = useState<IpPool | null>(null)

  /** Qidirilayotgan manzil — tugma bosilganda o'rnatiladi, har harfda emas */
  const [lookupIp, setLookupIp] = useState<string | null>(null)
  const [ipInput, setIpInput] = useState('')

  /*
    ⚠️ FOND RO'YXATINING QIDIRUVI VA FILTRI — MANZILDA.

    `GET /api/inventory/ip-pools` (`IpPoolResource#list()`) birorta
    parametr qabul qilmaydi va TO'LIQ ro'yxat qaytaradi, ya'ni bu
    yerdagi filtr hamma qatorni ko'radi va hech nimani yashirmaydi.
    Shu sababdan jadvalda sahifalash ham yo'q (u allaqachon shunday
    edi).

    ⚠️ URL kaliti `q` — sahifadagi IKKINCHI qidiruv (o'ngdagi IP
    izlash) undan ALOHIDA va o'z holatida qoladi: ular ikki BOSHQA
    savol va bitta parametrni baham ko'rsa, biri ikkinchisini
    o'chirardi.
  */
  const { state: tableState, patch: patchTable, reset: resetTable } = useTableUrlState()
  /**
   * ⚠️ Sukut — FAQAT AMALDAGILAR (`poolStateFilter`). O'chirilgan fond
   * yashirinadi, lekin YO'QOLMAYDI: filtrda o'z tanlovi bor va uni
   * qayta yoqish amali o'sha qatorda turadi.
   */
  const poolState = poolStateFilter(tableState)
  /** Operator ATAYLAB tanladimi — sukutdagi torayish «filtr» deb sanalmaydi */
  const poolStatePicked = tableState.filters[POOL_STATE_FILTER]?.[0] !== undefined
  const poolQuery = tableState.search.trim().toLowerCase()

  const showAllPools = () =>
    patchTable({ filters: { ...tableState.filters, [POOL_STATE_FILTER]: [POOL_STATE_ALL] } })

  const [form] = Form.useForm<{ name: string; cidr: string; gateway?: string; vlanId?: number }>()
  const [editForm] = Form.useForm<{ name: string; gateway?: string; vlanId?: number }>()

  const pools = useQuery({ queryKey: queryKeys.ipPools.all, queryFn: fetchIpPools })

  const allPools = useMemo(() => pools.data ?? [], [pools.data])
  const visiblePools = useMemo(
    () =>
      allPools.filter((row) => {
        if (poolState !== POOL_STATE_ALL && row.state !== poolState) return false
        if (poolQuery === '') return true
        return (
          row.name.toLowerCase().includes(poolQuery) || row.cidr.toLowerCase().includes(poolQuery)
        )
      }),
    [allPools, poolState, poolQuery],
  )
  const poolsFiltered = poolQuery !== '' || poolStatePicked
  /** Sukut torayish ham SANALADI — sabab `TenantsPage` dagi bir xil blokda */
  const poolsHidden = allPools.length - visiblePools.length

  const active = useQuery({
    queryKey: queryKeys.ipPools.byIp(lookupIp ?? ''),
    queryFn: () => fetchActiveByIp(lookupIp as string),
    enabled: lookupIp !== null,
  })

  const history = useQuery({
    queryKey: queryKeys.ipPools.history(lookupIp ?? ''),
    queryFn: () => fetchIpHistory(lookupIp as string),
    enabled: lookupIp !== null,
  })

  const refresh = () => void queryClient.invalidateQueries({ queryKey: queryKeys.ipPools.all })

  /*
    ⚠️ IKKI OYNANING XATOSI SAHIFAGA CHIQMAYDI (`VoucherVoidModal` naqshi).

    Ilgari `create` va `update` xatosi shu sahifadagi bannerga yozilardi,
    oyna esa OCHIQ qolardi — xabar modal niqobi ortida turardi va
    operator faqat spinner to'xtaganini ko'rardi. Endi u oynaning
    ichida, amal tugmasi bilan bir ekranda.

    Banner sahifada QOLADI, chunki `toggle` oyna ICHIDA emas — u
    jadvaldagi qator tugmasi va uning xatosini ko'rsatadigan boshqa joy
    yo'q.
  */
  const create = useMutation({
    mutationFn: (values: { name: string; cidr: string; gateway?: string; vlanId?: number }) =>
      createIpPool(values),
    onSuccess: () => {
      setCreateOpen(false)
      form.resetFields()
      refresh()
    },
  })

  const update = useMutation({
    mutationFn: (values: { name: string; gateway?: string; vlanId?: number }) =>
      updateIpPool(editing?.id ?? '', values),
    onSuccess: () => {
      setEditing(null)
      editForm.resetFields()
      refresh()
    },
  })

  const closeCreate = () => {
    create.reset()
    setCreateOpen(false)
  }

  const closeEdit = () => {
    update.reset()
    setEditing(null)
  }

  const toggle = useMutation({
    mutationFn: (pool: IpPool) =>
      setIpPoolState(pool.id, pool.state === 'ACTIVE' ? 'DISABLED' : 'ACTIVE'),
    onSuccess: refresh,
    onError: (err) => setError(translateError(err, t)),
  })

  const poolColumns: ColumnsType<IpPool> = [
    {
      // Fondlar ro'yxati sahifalanmaydi — raqam `index` dan.
      key: '__rowNo',
      title: t('dataTable.rowNo'),
      width: 56,
      align: 'center',
      render: (_v, _r, index) => <Typography.Text type="secondary">{index + 1}</Typography.Text>,
    },
    {
      key: 'name',
      title: t('ipPool.name'),
      width: 160,
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Typography.Text strong>{row.name}</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }} code>
            {row.cidr}
          </Typography.Text>
        </Space>
      ),
    },
    {
      key: 'gateway',
      title: t('ipPool.gateway'),
      width: 120,
      /*
        ⚠️ Shlyuzsiz fond — XATO EMAS, lekin uni bo'sh katak qilib
        qoldirish "ma'lumot yetishmayapti" bo'lib ko'rinardi. Aslida
        bu "shlyuz belgilanmagan" degan ATAYLAB qo'yilgan holat.
      */
      render: (_, row) =>
        row.gateway ?? <Typography.Text type="secondary">{t('ipPool.noGateway')}</Typography.Text>,
    },
    {
      key: 'vlan',
      title: 'VLAN',
      width: 70,
      render: (_, row) => row.vlanId ?? '—',
    },
    {
      key: 'state',
      title: t('subscribers.columns.status'),
      width: 110,
      render: (_, row) => (
        <Tag color={row.state === 'ACTIVE' ? 'green' : 'default'}>
          {t(`ipPool.state.${row.state}`)}
        </Tag>
      ),
    },
    {
      key: 'actions',
      title: '',
      width: 56,
      align: 'center',
      // ⚠️ `fixed` — jadvalda gorizontal scroll bor; usiz amallar
      // ekrandan chiqib ketardi (`VenuesPage` dagi bilan bir xil xato).
      fixed: 'right',
      render: (_, row) => (
        <RowActions
          items={[
            {
              key: 'edit',
              label: t('tariffs.edit'),
              onClick: () => {
                setEditing(row)
                editForm.setFieldsValue({
                  name: row.name,
                  gateway: row.gateway ?? undefined,
                  vlanId: row.vlanId ?? undefined,
                })
              },
              permission: 'inventory.ip.manage',
            },
            {
              key: 'toggle',
              label: row.state === 'ACTIVE' ? t('ipPool.disable') : t('ipPool.enable'),
              onClick: () => toggle.mutate(row),
              loading: toggle.isPending,
              permission: 'inventory.ip.manage',
            },
          ]}
        />
      ),
    },
  ]

  const historyColumns: ColumnsType<IpAllocation> = [
    {
      key: '__rowNo',
      title: t('dataTable.rowNo'),
      width: 56,
      align: 'center',
      // Sahifalash yo'q — `index` allaqachon to'liq javobdagi o'rin
      render: (_v, _r, index) => <Typography.Text type="secondary">{index + 1}</Typography.Text>,
    },
    {
      key: 'owner',
      title: t('ipPool.owner'),
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Tag>{t(`ipPool.ownerType.${row.ownerType}`)}</Tag>
          <Typography.Text type="secondary" style={{ fontSize: 11 }} code>
            {/* Qisqartirish YAGONA joyda — `shared/lib/shortId.ts` (B12) */}
            {/* ⚠️ `ownerId` sxemada `nullish()` — `null` HAM, `undefined`
                HAM bo'lishi mumkin (`ipPoolApi.allocationSchema`), shuning
                uchun `=== null` yetarli emas. */}
            {row.ownerId ? shortId(row.ownerId) : t('common.dash')}
          </Typography.Text>
        </Space>
      ),
    },
    {
      key: 'allocatedAt',
      title: t('ipPool.allocatedAt'),
      width: 170,
      render: (_, row) => formatDateTime(row.allocatedAt),
    },
    {
      key: 'releasedAt',
      title: t('ipPool.releasedAt'),
      width: 180,
      /*
        ⚠️ `null` — manzil HOZIR BAND, "ma'lumot yo'q" EMAS. Ikkalasini
        bir xil ("—") ko'rsatish operatorni "bo'shatilgan" deb
        adashtirardi va u manzilni boshqasiga berib yuborardi.
      */
      render: (_, row) =>
        row.releasedAt ? (
          formatDateTime(row.releasedAt)
        ) : (
          <Tag color="green">{t('ipPool.stillHeld')}</Tag>
        ),
    },
  ]

  return (
    <>
      <PageHeader
        title={t('ipPool.title')}
        description={t('ipPool.subtitle')}
        extra={
          <Can permission="inventory.ip.manage">
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
              {t('ipPool.create')}
            </Button>
          </Can>
        }
      />

      {error ? (
        <Alert
          type="error"
          showIcon
          closable
          message={error}
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      ) : null}

      <Row gutter={[16, 16]}>
        <Col xs={24} xl={14}>
          <Card title={t('ipPool.poolsTitle')}>
            {/*
              ⚠️ Yiqilgan so'rov BO'SH RO'YXAT bo'lib ko'rinmasin: jadval
              o'z `emptyText` i bilan "fond yaratilmagan" deb yozardi va
              operator uni ISHONCHLI javob deb o'qirdi (`VouchersPage`
              dagi bilan bir xil naqsh).
            */}
            {pools.isError ? (
              <Alert
                type="error"
                showIcon
                message={t('dataTable.loadFailed')}
                description={translateError(pools.error, t)}
                action={
                  <Button size="small" onClick={() => void pools.refetch()}>
                    {t('dataTable.retry')}
                  </Button>
                }
              />
            ) : (
              <>
              <Space wrap style={{ marginBottom: 12 }}>
                <UrlSearchInput placeholder={t('ipPool.searchPlaceholder')} width={220} />
                {/* ⚠️ `allowClear` YO'Q — sabab `TenantsPage` dagi bir xil blokda:
                    bo'sh maydon «filtr qo'yilmagan» deb o'qilardi. */}
                <Tooltip title={t('dataTable.closedHiddenHint')}>
                  <Select<string>
                    style={{ minWidth: 150 }}
                    aria-label={t('subscribers.columns.status')}
                    value={poolState}
                    onChange={(v: string) =>
                      patchTable({ filters: { ...tableState.filters, [POOL_STATE_FILTER]: [v] } })
                    }
                    options={[
                      ...POOL_STATES.map((s) => ({ value: s, label: t(`ipPool.state.${s}`) })),
                      { value: POOL_STATE_ALL, label: t('dataTable.stateAll') },
                    ]}
                  />
                </Tooltip>
                {poolsHidden > 0 || poolsFiltered ? (
                  <Space size={4}>
                    <Tag color="blue">
                      {t('roles.shownOfTotal', {
                        shown: visiblePools.length,
                        total: allPools.length,
                      })}
                    </Tag>
                    {poolsFiltered ? (
                      <Button size="small" type="link" onClick={resetTable}>
                        {t('dataTable.clearFilters')}
                      </Button>
                    ) : (
                      <Button size="small" type="link" onClick={showAllPools}>
                        {t('dataTable.stateAll')}
                      </Button>
                    )}
                  </Space>
                ) : null}
              </Space>
              <Table<IpPool>
                columns={poolColumns}
                dataSource={visiblePools}
                rowKey={(row) => row.id}
                loading={pools.isPending}
                size="middle"
                pagination={false}
                /*
                  56+160+120+70+110+56 = 572.

                  ⚠️ ENLAR QISQARTIRILDI: jadval sahifaning YARIM
                  ustunida turadi va unga ~640px tegadi. Ilgari yig'indi
                  720 edi — natijada "Holat" sarlavhasi "H" ga
                  kesilardi. Gorizontal scroll bo'lgani bilan, sarlavhasi
                  kesilgan ustunni operator umuman payqamaydi.

                  Amallar ustuni «uch nuqta» menyusiga yig'ilgach 170 dan
                  56 ga tushdi, tartib raqami esa 56 qo'shdi.
                */
                scroll={{ x: 572 }}
                locale={{
                  // ⚠️ Ikki xil bo'shlik AJRATILGAN: "fond yaratilmagan"
                  //    (tuzatish yo'li — yangi fond) va "filtr topmadi"
                  //    (tuzatish yo'li — filtrni tozalash).
                  // ⚠️ UCHINCHI SHOX: sukut ko'rinishda o'chirilgan fondlar
                  //    yashirin, ya'ni bo'sh jadval «fond yaratilmagan»
                  //    degani EMAS.
                  emptyText: poolsFiltered ? (
                    <EmptyState
                      variant="no-data"
                      title={t('dataTable.emptyFiltered')}
                      description={t('dataTable.emptyFilteredHint')}
                      actionLabel={t('dataTable.resetFilters')}
                      onAction={resetTable}
                    />
                  ) : poolsHidden > 0 ? (
                    <EmptyState
                      variant="no-data"
                      title={t('ipPool.empty')}
                      description={t('dataTable.closedHiddenHint')}
                      actionLabel={t('dataTable.stateAll')}
                      onAction={showAllPools}
                    />
                  ) : (
                    <EmptyState variant="no-data" title={t('ipPool.empty')} />
                  ),
                }}
              />
              </>
            )}
          </Card>
        </Col>

        <Col xs={24} xl={10}>
          <Card title={t('ipPool.lookupTitle')}>
            <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
              {t('ipPool.lookupHint')}
            </Typography.Paragraph>

            <Input.Search
              allowClear
              enterButton={<SearchOutlined />}
              placeholder="10.10.40.15"
              value={ipInput}
              onChange={(e) => setIpInput(e.target.value)}
              // ⚠️ Tarix jadvali endi sahifalanmaydi (quyidagi izoh),
              //    ya'ni "yangi manzil — eski sahifada qolib ketish"
              //    holati umuman yo'q.
              onSearch={(value) => setLookupIp(value.trim() || null)}
              style={{ marginBottom: 16 }}
            />

            {lookupIp === null ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('ipPool.lookupIdle')} />
            ) : (
              <>
                {/*
                  ⚠️ "Band emas" va "so'rov yiqildi" ALOHIDA ko'rsatiladi.
                  Backend bo'sh manzil uchun 404 qaytaradi va uni xato
                  deb ko'rsatish operatorni "tizim buzuq" degan xulosaga
                  olib kelardi — aslida javob TO'LIQ va aniq.
                */}
                {active.isError ? (
                  <Alert type="error" showIcon message={translateError(active.error, t)} />
                ) : active.data ? (
                  <Descriptions size="small" column={1} bordered style={{ marginBottom: 16 }}>
                    <Descriptions.Item label={t('ipPool.owner')}>
                      <Space>
                        <Tag>{t(`ipPool.ownerType.${active.data.ownerType}`)}</Tag>
                        <Typography.Text copyable code style={{ fontSize: 12 }}>
                          {active.data.ownerId ?? '—'}
                        </Typography.Text>
                      </Space>
                    </Descriptions.Item>
                    <Descriptions.Item label={t('ipPool.allocatedAt')}>
                      {formatDateTime(active.data.allocatedAt)}
                    </Descriptions.Item>
                  </Descriptions>
                ) : active.isPending ? null : (
                  <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message={t('ipPool.free')}
                    description={t('ipPool.freeHint')}
                  />
                )}

                <Typography.Title level={5}>{t('ipPool.historyTitle')}</Typography.Title>
                {/*
                  ⚠️ "Bu manzil hech kimda bo'lmagan" — HUQUQIY MUROJAATGA
                  beriladigan javob, ya'ni u yiqilgan so'rovdan chiqmasligi
                  kerak. Ilgari xato jadvalni bo'sh qoldirardi va
                  `emptyText` aynan shu xulosani yozardi. Yuqoridagi
                  `active.isError` allaqachon shunday ajratilgan — bu
                  jadval o'sha qorovulsiz qolgan edi.
                */}
                {history.isError ? (
                  <Alert
                    type="error"
                    showIcon
                    message={t('dataTable.loadFailed')}
                    description={translateError(history.error, t)}
                    action={
                      <Button size="small" onClick={() => void history.refetch()}>
                        {t('dataTable.retry')}
                      </Button>
                    }
                  />
                ) : (
                  <Table<IpAllocation>
                    columns={historyColumns}
                    dataSource={history.data ?? []}
                    rowKey={(row) => row.id}
                    loading={history.isPending}
                    size="small"
                    /*
                      ⚠️ SAHIFALASH OLIB TASHLANDI (JADVAL-QOIDASI §6).

                      `GET /api/inventory/ip-allocations/by-ip/{ip}/history`
                      yagona `limit` parametrini biladi — `page` yoki
                      `offset` YO'Q. Ya'ni bu yerdagi sahifalash butun
                      javob allaqachon xotirada turganda uni KESARDI va
                      "server sahifalayapti" degan taassurot berardi.

                      ⚠️ O'rniga JADVALNING O'ZI scroll bo'ladi
                      (`scroll.y`): yozuvlar soni `limit` bilan 100 tada
                      cheklangan, ya'ni ro'yxat cheksiz o'smaydi, lekin
                      kartochkani ham cho'zib yubormaydi.
                    */
                    pagination={false}
                    scroll={{ y: 320 }}
                    locale={{ emptyText: t('ipPool.noHistory') }}
                  />
                )}
              </>
            )}
          </Card>
        </Col>
      </Row>

      <Modal
        open={createOpen}
        title={t('ipPool.create')}
        onCancel={closeCreate}
        onOk={() => submitOnValid(form, (values) => create.mutate(values))}
        confirmLoading={create.isPending}
        okText={t('operation.create')}
        cancelText={t('common.cancel')}
        destroyOnHidden
      >
        {/* Xato SHU YERDA qoladi — oyna ochiq, forma tuzatish uchun qo'l ostida */}
        {create.isError ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message={translateError(create.error, t)}
          />
        ) : null}

        <Form form={form} layout="vertical" preserve={false} disabled={create.isPending}>
          <Form.Item
            name="name"
            label={t('ipPool.name')}
            rules={[{ required: true, message: t('ipPool.nameRequired') }]}
          >
            <Input autoFocus placeholder={t('ipPool.namePlaceholder')} />
          </Form.Item>
          <Form.Item
            name="cidr"
            label="CIDR"
            extra={t('ipPool.cidrHint')}
            rules={[{ required: true, message: t('ipPool.cidrRequired') }]}
          >
            <Input placeholder="10.10.40.0/24" />
          </Form.Item>
          <Form.Item name="gateway" label={t('ipPool.gateway')}>
            <Input placeholder="10.10.40.1" />
          </Form.Item>
          <Form.Item name="vlanId" label="VLAN">
            <InputNumber min={1} max={4094} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={editing !== null}
        title={t('ipPool.editTitle')}
        onCancel={closeEdit}
        onOk={() => submitOnValid(editForm, (values) => update.mutate(values))}
        confirmLoading={update.isPending}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        destroyOnHidden
      >
        {/*
          ⚠️ CIDR bu yerda YO'Q — backend uni o'zgartirishga yo'l
          qo'ymaydi (`ipPoolApi.ts` da sababi). Maydonni ko'rsatib,
          keyin "saqlanmadi" deb aytishdan ko'ra, umuman ko'rsatmaslik
          va NEGA ekanini yozib qo'yish to'g'riroq.
        */}
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('ipPool.cidrLocked')}
          description={t('ipPool.cidrLockedHint')}
        />

        {update.isError ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message={translateError(update.error, t)}
          />
        ) : null}

        <Form form={editForm} layout="vertical" preserve={false} disabled={update.isPending}>
          <Form.Item
            name="name"
            label={t('ipPool.name')}
            rules={[{ required: true, message: t('ipPool.nameRequired') }]}
          >
            <Input autoFocus />
          </Form.Item>
          <Form.Item name="gateway" label={t('ipPool.gateway')}>
            <Input />
          </Form.Item>
          <Form.Item name="vlanId" label="VLAN">
            <InputNumber min={1} max={4094} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}
